import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { animated } from 'react-spring';
import DOMPurify from 'dompurify';

import { Product } from '../interfaces';
import { Button, MoreInformationButton } from './Buttons';
import { useAppData, useIntl } from '../providers';

const useStyles = createUseStyles({
    product: {
        padding: '2rem',
        backgroundColor: '#e6e6e6',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        flex: 0,
        paddingRight: '64px',
        '& h3': {
            fontFamily: '"Helvetica Neue LT W01_65 Md", "Helvetica Neue", Helvetica, Arial, sans-serif',
            fontSize: '1em',
            fontWeight: '400',
            lineHeight: '1.5em',
            marginBottom: '0',

            '@media (min-width: 1520px)': {
                lineHeight: '1.6em',
            },
        },
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'flex-start',
        flex: 0,
        margin: '38px -1rem 0',
        '& > *': {
            flex: 1,
            margin: '0 1rem',
        },
    },
    logo: {
        '& img': {
            maxWidth: '100px',
            maxHeight: '100px',
            height: 'auto',
        },
        '@media (min-width: 640px)': {
            position: 'absolute',
            right: '1rem',
            top: '1rem',
        },
    },
    icon: {
        position: 'absolute',
        right: '1rem',
        top: '1rem',
        maxWidth: '64px',
        backgroundColor: 'var(--iconColor, #000)',
        borderRadius: '50%',
        color: '#fff',
        aspectRatio: '1/1',

        '& svg': {
            display: 'block',
            width: '100%',
            height: 'auto',
        },
    },
    button: {
        width: '100%',
        border: '2px solid var(--primary-color)',
        color: 'var(--primary-color)',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        '&:disabled': {
            border: '2px solid var(--deactivated-color)',
            color: 'var(--deactivated-color)',
            cursor: 'not-allowed',
        }
    },
    buttonShowAll: {
        marginTop: '10px'
    }
});

interface ProductProps {
    product: Product;
    style?: object;
}

const JobCard = ({ product, style = {} }: ProductProps, ref?: React.Ref<any>) => {
    const classes = useStyles();
    const { translate } = useIntl();
    const {
        selectedCategory,
    } = useAppData();

    const isRootCategory = () => {
        return selectedCategory.parentPath ? false : true;
    }

    return (
        <animated.article className={classes.product} style={style} ref={ref}>
            <header className={classes.header}>
                <h3>{product.title}</h3>
            </header>
            {product.icon && (
                <div
                    className={classes.icon}
                    style={{ '--iconColor': product.color } as React.CSSProperties}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.icon) }}
                ></div>
            )}
            <div>{ product.location}</div>
            <footer className={classes.actions}>
                <span>
                    <MoreInformationButton href={product.href} />
                </span>
                <span>
                    <Button
                        href={product.href}
                        label={
                            product.isNotAvailable ?
                                translate('productCard.action.isNotAvailable', 'Not available') :
                                translate('productCard.action.order', 'order')
                        }
                        disabled={product.isNotAvailable}
                    />

                    {product.isNotAvailable && !isRootCategory() && (
                        <Button
                            className={classes.buttonShowAll}
                            href={product.href}
                            label={translate('productCard.action.alternativeProducts', 'Altenative products')}
                        />
                    )}
                </span>
            </footer>
        </animated.article>
    );
};

export default React.forwardRef(JobCard);
